import { FetchClient, storeClient } from '@/shared/api/client';

import {
  AccountOrderDetails,
  AccountOrdersListResponse,
  AccountShowResponse,
  ChangePasswordPayload,
  ChangePasswordResponse,
  CustomerDetailsUpdatePayload,
  ForgotPasswordConfirmPayload,
  ForgotPasswordResponse,
  RefreshTokenResponse,
  SignInPayload,
  SignInResponse,
  SignUpPayload,
  SignUpResponse,
} from './types';

export const signUp = async (
  payload: SignUpPayload,
  fetch?: FetchClient
): Promise<SignUpResponse> => {
  try {
    const result = await storeClient<SignUpResponse>(
      '/customer/create-account',
      {
        method: 'POST',
        body: payload,
      },
      fetch
    );

    return result;
  } catch {
    return {
      customerId: '',
      locale: 'pl-PL',
    };
  }
};

export const signIn = async (
  payload: SignInPayload,
  fetch?: FetchClient
): Promise<SignInResponse | false | null> => {
  try {
    const result = await storeClient<{ code: number } | SignInResponse>(
      '/customer/login',
      {
        method: 'POST',
        body: payload,
      },
      fetch
    );

    if ((result as { code: number })?.code === 301) {
      return false;
    }

    return result as SignInResponse;
  } catch {
    return null;
  }
};

export const accountConfirm = async (
  confirmationCode: string,
  fetch?: FetchClient
): Promise<boolean> => {
  try {
    await storeClient(
      '/customer/create-account-confirm',
      {
        method: 'POST',
        body: { confirmationCode },
      },
      fetch
    );

    return true;
  } catch {
    return false;
  }
};

export const refreshToken = async (
  refreshToken: string,
  fetch?: FetchClient
): Promise<RefreshTokenResponse | null> => {
  try {
    const response = await storeClient<RefreshTokenResponse>(
      '/customer/refresh-token',
      {
        method: 'POST',
        body: { refreshToken },
      },
      fetch
    );

    return response;
  } catch {
    return null;
  }
};

export const fetchAccountShow = async (
  fetch?: FetchClient
): Promise<AccountShowResponse | null> => {
  try {
    const response = await storeClient<AccountShowResponse>(
      '/customer/account-show',
      {
        method: 'GET',
      },
      fetch
    );

    return response;
  } catch {
    return null;
  }
};

export const forgotPassword = async (
  email: string,
  fetch?: FetchClient
): Promise<ForgotPasswordResponse | null> => {
  try {
    const response = await storeClient<ForgotPasswordResponse>(
      '/customer/forgot-password',
      {
        method: 'POST',
        body: { email },
      },
      fetch
    );

    return response;
  } catch {
    return null;
  }
};

export const forgotPasswordConfirm = async (
  body: ForgotPasswordConfirmPayload,
  fetch?: FetchClient
): Promise<boolean> => {
  try {
    await storeClient(
      '/customer/forgot-password-confirm',
      {
        method: 'POST',
        body,
      },
      fetch
    );

    return true;
  } catch {
    return false;
  }
};

export const fetchAccountOrderList = async (
  page: number,
  fetch?: FetchClient
): Promise<AccountOrdersListResponse> => {
  try {
    const response = await storeClient<AccountOrdersListResponse>(
      '/customer/list-orders',
      {
        method: 'POST',
        body: {
          page,
          limit: 10,
        },
      },
      fetch
    );

    return response;
  } catch {
    return {
      count: 0,
      data: [],
    };
  }
};

export const logout = async (fetch?: FetchClient): Promise<boolean> => {
  try {
    await storeClient(
      '/customer/logout',
      {
        method: 'POST',
      },
      fetch
    );

    return true;
  } catch {
    return false;
  }
};

export const customerDetailsUpdate = async (
  body: CustomerDetailsUpdatePayload,
  fetch?: FetchClient
): Promise<boolean> => {
  try {
    await storeClient(
      '/customer/details-update',
      {
        method: 'POST',
        body,
      },
      fetch
    );

    return true;
  } catch {
    return false;
  }
};

export const fetchCustomerOrder = async (
  orderId: string,
  fetch?: FetchClient
): Promise<AccountOrderDetails | null> => {
  try {
    const response = await storeClient<AccountOrderDetails>(
      '/customer/get-order',
      {
        method: 'POST',
        body: {
          orderId,
        },
      },
      fetch
    );

    return response;
  } catch {
    return null;
  }
};

export const changePassword = async (
  payload: ChangePasswordPayload,
  fetch?: FetchClient
): Promise<SignInResponse | null | false> => {
  try {
    const result = await storeClient<ChangePasswordResponse>(
      '/customer/change-password',
      {
        method: 'POST',
        body: payload,
      },
      fetch
    );

    return result;
  } catch (e) {
    const error = Object.getOwnPropertyDescriptors(e)?.message?.value;

    if (!error) {
      return null;
    }

    try {
      const parsedError = JSON.parse(error);

      if (parsedError?.code === 401) {
        return false;
      }

      return null;
    } catch {
      return null;
    }
  }
};
